import { FC } from 'react';
import { LinkConfig, PageProps } from 'types/WebMicrofrontends';
import { CategoryPage, CourseCarouselItem } from 'types';
import { GET_CATEGORY_DATA, GET_CATEGORIES } from 'queries';
import { CoursesSection } from 'components/02-molecules/CoursesSection';
import { DarkFullWidthCTA } from 'components/02-molecules/DarkFullWidthCTA';
import { useRouter } from 'next/router';
import { GetStaticPaths } from 'next';
import { OUTSIDE_ONLINE_URLS, APPLICATION_URL } from 'lib/constants';
import { NextSeo } from 'next-seo';
import {
  useUserType,
  joinOutside,
  useIsOutsideDigitalUser
} from 'components/04-templates';
import { parsePageSeo } from 'utils';
import { initializeApollo } from 'helpers/hooks';
import { useCache } from 'helpers/hooks/useCache';
import { routes } from 'lib/routes';
import Head from 'next/head';
import { useApolloClient } from '@apollo/client';
import { openUpgradeOfferPianoModal } from 'lib/piano/helpers';

const ChannelCategory: FC<PageProps<CategoryPage>> = ({ props, variables }) => {
  const { isFallback, asPath } = useRouter();

  const { isOutsidePlusUser } = useUserType();

  const isOutsideDigitalUser = useIsOutsideDigitalUser();

  // this gives us the same Apollo Client that was passed into the ApolloProvider in LearnFederationProvider.tsx
  const client = useApolloClient();

  client.writeQuery({
    query: GET_CATEGORY_DATA,
    variables,
    data: {
      props
    }
  });

  const {
    paywall,
    cta,
    categoryName,
    courses,
    categorySeo,
    standaloneLessons
  } = useCache(variables);

  if (isFallback) {
    return <div>Loading...</div>;
  }
  const carouselContent = [...courses, ...standaloneLessons];

  const CTAButtonConfigOnClick = isOutsideDigitalUser
    ? openUpgradeOfferPianoModal
    : joinOutside;

  const CTALinkConfig: LinkConfig = {
    ...(!isOutsidePlusUser
      ? { onClick: CTAButtonConfigOnClick }
      : { href: OUTSIDE_ONLINE_URLS.OUTSIDE_PLUS }),
    text: cta?.buttonText,
    target: '_self'
  };

  const schemaData = [
    {
      '@context': 'https://schema.org/',
      '@type': 'CollectionPage',
      '@id': `${APPLICATION_URL}${asPath.split('?')[0]}`
    },
    {
      '@context': 'https://schema.org/',
      '@type': 'ItemList',
      name: `${categoryName} Courses and Lessons`,
      numberOfItems: carouselContent.length,
      itemListElement: courses
        .map((course, index) => ({
          '@type': 'Course',
          position: index + 1,
          url: `${APPLICATION_URL}${routes.courseIntroduction(course.name)}`,
          name: course?.title,
          description: course?.excerpt,
          isAccessibleForFree: 'False',
          provider: {
            '@type': 'Organization',
            name: 'Outside',
            url: 'https://www.outsideonline.com/'
          }
        }))
        .concat(
          standaloneLessons.map((standaloneLesson, index) => ({
            '@type': 'Course',
            position: courses.length + index + 1,
            url: `${APPLICATION_URL}${routes.standaloneLesson(
              standaloneLesson?.name
            )}`,
            name: standaloneLesson?.title,
            description: standaloneLesson?.overview?.description,
            isAccessibleForFree: 'False',
            provider: {
              '@type': 'Organization',
              name: 'Outside',
              url: 'https://www.outsideonline.com/'
            }
          }))
        )
    }
  ];

  return (
    <section className="container mx-auto">
      {categorySeo && <NextSeo {...parsePageSeo({ ...categorySeo, asPath })} />}
      <Head>
        <script
          type="application/ld+json"
          id={`learn-category-${categoryName}-schema`}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
          key={`learn-category-${categoryName}-schema`}
        />
      </Head>

      {courses && (
        <CoursesSection courses={carouselContent as CourseCarouselItem[]} />
      )}
      {!isOutsidePlusUser && (
        <div className="mx-auto max-w-5xl py-13">
          <DarkFullWidthCTA
            dek={cta?.description}
            linkConfig={CTALinkConfig}
            priceDisclaimer={cta?.priceDisclaimer}
            paywall={paywall}
            headingText={cta?.headline}
          />
        </div>
      )}
    </section>
  );
};

export const getStaticProps = async ({ params }) => {
  const { category } = params;
  const apolloClient = initializeApollo();

  const { data, error } = await apolloClient.query<CategoryPage>({
    query: GET_CATEGORY_DATA,
    variables: { category }
  });
  const { props } = data;
  if (error) {
    throw new Error(
      `Could not build static page for learn categories: ${category}. Errors: ${error}`
    );
  }

  return {
    props: {
      props,
      variables: { category }
    },
    revalidate: 300
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const apolloClient = initializeApollo();
  const { data, error } = await apolloClient.query({
    query: GET_CATEGORIES
  });

  if (error) {
    throw new Error(
      `Could not build static paths for learn categories. ${error.message}`
    );
  }

  const { categories } = data;

  return {
    paths: categories.map(({ name }) => ({ params: { category: name } })),
    fallback: true
  };
};

export default ChannelCategory;
